import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Row, message, Input, Select, Checkbox, Button, Card } from "antd";
import { PictureOutlined, FileTextOutlined } from "@ant-design/icons";
import Modal from "antd/lib/modal/Modal";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./MessageModal.css";
import { CloseOutlined } from "@ant-design/icons";
import { fetchAudienceList, createAudience } from "services/audienceService";
import { sendEmailCampaign } from "services/emailCampaignService";
import { apiGetClubSite } from "services/customWebpageService";
import CreateAudienceModal from "../../AudiencePage/CreateAudienceModal/CreateAudienceModal";

function MessageModal(props) {
  const { show, proceed, title, dataList } = props;

  const [selectedToAudiences, setSelectedToAudiences] = useState([]);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [from, setFrom] = useState(props?.from || "");
  const [templateContent, setTemplateContent] = useState("");
  const [selFile, setSelFile] = useState(null);
  const [isSending, setIsSending] = useState(false);
  // Wizard state
  const [step, setStep] = useState(1);
  const [templateType, setTemplateType] = useState("");
  const [useDefaultLogo, setUseDefaultLogo] = useState(true);
  const [logoFile, setLogoFile] = useState(null);
  const [assetFile, setAssetFile] = useState(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);
  const [assetBlobUrl, setAssetBlobUrl] = useState(null);
  const [audienceList, setAudienceList] = useState([]);
  const [showCreateAudience, setShowCreateAudience] = useState(false);
  const [defaultLogoUrl, setDefaultLogoUrl] = useState("");

  // Load audiences when modal opens
  useEffect(() => {
    async function loadAudiences() {
      const token = props?.token;
      const clubId = props?.club_id;
      if (token && show) {
        const list = await fetchAudienceList(token, clubId);
        // Only show email audiences in the email modal
        const emailOnly = (Array.isArray(list) ? list : []).filter(
          (a) => (a.channel || "email") !== "sms"
        );
        setAudienceList(emailOnly);
      }
    }
    if (show) {
      // ensure from is set to incoming prop when opening
      setFrom(props?.from || "");
      loadAudiences();
      // Load default logo from club site settings
      (async () => {
        try {
          const site = await apiGetClubSite({ token: props?.token, club_id: props?.club_id });
          if (site) {
            const side = site.default_email_logo;
            const logoUrl = side === "left" ? site.logo_left : side === "right" ? site.logo_right : "";
            if (logoUrl) {
              const absUrl = logoUrl.startsWith("http") ? logoUrl : `${(process.env.REACT_APP_API || "").replace("/api/", "")}/${logoUrl}`;
              setDefaultLogoUrl(absUrl);
            } else {
              setDefaultLogoUrl("");
            }
          }
        } catch (e) {
          console.error("Failed to load default logo:", e);
        }
      })();
    }
  }, [show, props?.token]);

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setSelectedToAudiences([]);
      setEditorState(EditorState.createEmpty());
      setSubject("");
      setContent("");
      setTemplateContent("");
      setSelFile(null);
      setStep(1);
      setTemplateType("");
      setUseDefaultLogo(true);
      setLogoFile(null);
      setAssetFile(null);
      // Clean up blob URLs
      if (logoBlobUrl) {
        URL.revokeObjectURL(logoBlobUrl);
        setLogoBlobUrl(null);
      }
      if (assetBlobUrl) {
        URL.revokeObjectURL(assetBlobUrl);
        setAssetBlobUrl(null);
      }
    }
  }, [show]);

  // Create blob URLs when files change
  useEffect(() => {
    let currentUrl = null;
    
    if (logoFile && !useDefaultLogo) {
      if (logoBlobUrl) {
        URL.revokeObjectURL(logoBlobUrl);
      }
      currentUrl = URL.createObjectURL(logoFile);
      setLogoBlobUrl(currentUrl);
    } else if (useDefaultLogo || !logoFile) {
      if (logoBlobUrl) {
        URL.revokeObjectURL(logoBlobUrl);
        setLogoBlobUrl(null);
      }
    }
    
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [logoFile, useDefaultLogo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let currentUrl = null;
    
    if (assetFile) {
      if (assetBlobUrl) {
        URL.revokeObjectURL(assetBlobUrl);
      }
      currentUrl = URL.createObjectURL(assetFile);
      setAssetBlobUrl(currentUrl);
    } else {
      if (assetBlobUrl) {
        URL.revokeObjectURL(assetBlobUrl);
        setAssetBlobUrl(null);
      }
    }
    
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [assetFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const onEditorStateChange = (e) => {
    if (e != null) {
      let selStr = draftToHtml(convertToRaw(e.getCurrentContent()));
      // Always combine template with editor content (editor content replaces previous editor content)
      // templateContent is set in onNext and doesn't change
      const combinedContent = templateContent + (selStr && selStr.trim() ? `<div>${selStr}</div>` : "");
      setContent(combinedContent);
      setEditorState(e);
    }
  };

  // Helper function to convert File to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Extract emails from selected audiences
  const getEmailsFromAudiences = (audienceIds) => {
    const emails = new Set();
    const audienceNames = [];
    audienceIds.forEach((audienceId) => {
      const audience = audienceList.find((a) => a.id === audienceId);
      if (audience) {
        audienceNames.push(audience.name);
        if (Array.isArray(audience.emails)) {
          audience.emails.forEach((email) => emails.add(email));
        }
      }
    });
    return { emails: Array.from(emails), audienceNames };
  };

  const handleCreateAudienceSubmit = async (formData) => {
    const type = formData.createFrom === "PROGRAM" ? 1 : formData.createFrom === "REGISTRATION" ? 2 : formData.createFrom === "TEAM" ? 4 : 3;
    const payload = {
      token: props.token,
      club_id: props.club_id,
      audience_name: formData.name,
      type,
    };
    if (formData.channel) payload.channel = formData.channel;
    if (type === 3) {
      if (formData.channel === "sms") {
        payload.phone_numbers = (formData.phoneNumbers || "").split(',').map(x => x.trim().replace(/[^+\d]/g, '')).filter(x => x);
      } else {
        payload.emails = (formData.emails || "").split(',').map(x => x.trim()).filter(x => x);
      }
    } else if (type === 2) {
      const searchString = formData.registrationNames
        ? (Array.isArray(formData.registrationNames) ? formData.registrationNames.join(',') : formData.registrationNames)
        : '';
      payload.search_string = searchString;
      payload.registration_id = (formData.registrationIds || []).join(',');
    } else if (type === 1) {
      payload.search_string = (formData.programIds || []).join(',');
      payload.registration_id = formData.registrationIdForProgram;
    } else if (type === 4) {
      payload.team_id = formData.teamId;
    }
    await createAudience(payload);
    // Reload audiences and auto-select the newly created one
    const list = await fetchAudienceList(props.token, props.club_id);
    const emailOnly = (Array.isArray(list) ? list : []).filter(
      (a) => (a.channel || "email") !== "sms"
    );
    setAudienceList(emailOnly);
    // The newest audience is likely the one just created - find it by name
    const newAud = emailOnly.find((a) => a.name === formData.name);
    if (newAud) {
      setSelectedToAudiences((prev) => [...prev, newAud.id]);
    }
    message.success("Audience created successfully!");
  };

  const onCreate = async () => {
    if (subject.trim() === "") {
      message.error("Please input subject");
      return;
    }
    if (selectedToAudiences.length === 0) {
      message.error("Please select at least one audience");
      return;
    }
    if (!content || content.trim() === "") {
      message.error("Please input content");
      return;
    }

    setIsSending(true);
    try {
      // Extract emails from audiences
      const { emails: toEmails, audienceNames: allAudienceNames } = getEmailsFromAudiences(selectedToAudiences);

      if (toEmails.length === 0) {
        message.error("Selected audiences have no email addresses");
        setIsSending(false);
        return;
      }

      // Prepare attachment if present
      let attachment = null;
      if (selFile) {
        try {
          const base64Data = await fileToBase64(selFile);
          attachment = {
            file_name: selFile.name,
            file_base64: base64Data,
          };
        } catch (error) {
          message.error("Failed to process attachment");
          setIsSending(false);
          return;
        }
      }

      // Build template HTML with placeholder tokens
      const templateHtml = buildFinalHtml();
      // Extract editor content (remove template preview HTML)
      const editorContent = content.replace(templateContent, '').trim();
      const finalHtml = templateHtml + (editorContent ? `<div>${editorContent}</div>` : "");

      // Convert logo/asset images to base64 for backend to host
      let logoBase64 = null;
      if (templateType !== "message-only") {
        if (useDefaultLogo && defaultLogoUrl) {
          logoBase64 = "default:" + defaultLogoUrl;
        } else if (useDefaultLogo) {
          logoBase64 = "default";
        } else if (logoFile) {
          logoBase64 = await fileToBase64(logoFile);
        }
      }
      let assetBase64 = null;
      if ((templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && assetFile) {
        assetBase64 = await fileToBase64(assetFile);
      }

      // Prepare payload
      const payload = {
        token: props.token,
        club_id: props.club_id,
        from: from && from.trim() ? from.trim() : undefined,
        subject: subject.trim(),
        html: finalHtml,
        to_emails: toEmails,
        audience_names: allAudienceNames,
        attachment: attachment,
        logo_base64: logoBase64,
        asset_base64: assetBase64,
      };

  // Remove undefined fields
  Object.keys(payload).forEach((key) => payload[key] === undefined && delete payload[key]);

  // Send campaign
  const result = await sendEmailCampaign(payload);

      message.success(`Email sent successfully to ${result.recipients_count || toEmails.length} recipients!`);
      
      proceed({ success: true, campaign_id: result.campaign_id });
    } catch (error) {
      console.error("[SendEmailCampaign] error:", error);
      message.error(error || "Failed to send email campaign");
    } finally {
      setIsSending(false);
    }
  };
  const onDropImage = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (files[0].size / 1000 / 1000 > 5) {
      message.error("File size should not be greater than 5Mb.");
      return;
    }
    setSelFile(files[0]);
  };

  const onUploadLogo = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setLogoFile(files[0]);
    setUseDefaultLogo(false);
  };
  const onUploadAsset = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setAssetFile(files[0]);
  };

  // Build preview HTML using blob URLs (for display only)
  const buildPreviewHtml = () => {
    const logoSrc = useDefaultLogo
      ? (defaultLogoUrl || "/assets/images/logo.png")
      : (logoBlobUrl || "/assets/images/logo.png");
    const assetSrc = assetBlobUrl || null;
    let html = `<div style="text-align:center;padding:16px">`;
    if (templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      html += `<img src="${logoSrc}" alt="Logo" style="max-width:180px;margin-bottom:16px"/>`;
    }
    if (templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      if (assetSrc) html += `<div><img src="${assetSrc}" alt="Image" style="max-width:100%"/></div>`;
    }
    html += `</div>`;
    return html;
  };

  // Build final HTML with placeholder tokens (backend replaces with hosted URLs)
  const buildFinalHtml = () => {
    let html = `<div style="text-align:center;padding:16px">`;
    if (templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      html += `<img src="{{LOGO_URL}}" alt="Logo" style="max-width:180px;margin-bottom:16px"/>`;
    }
    if (templateType === "logo-with-image" || templateType === "logo-with-image-and-message") {
      html += `<div><img src="{{ASSET_URL}}" alt="Image" style="max-width:100%"/></div>`;
    }
    html += `</div>`;
    return html;
  };

  const onNext = () => {
    if (subject.trim() === "") {
      message.error("Please input subject");
      return;
    }
    if (selectedToAudiences.length === 0) {
      message.error("Please select at least one audience");
      return;
    }
    if (!templateType) {
      message.error("Please select a template");
      return;
    }
    // Only validate logo upload for templates that require logos
    if ((templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && !useDefaultLogo && !logoFile) {
      message.error("Please upload a logo or check 'Use default'");
      return;
    }
    // Only validate image upload for templates that require images
    if ((templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && !assetFile) {
      message.error("Please upload an image for the selected template");
      return;
    }
    // Build preview HTML with blob URLs for display
    const templateHtml = buildPreviewHtml();
    setTemplateContent(templateHtml);
    setContent(templateHtml);
    setStep(2);
  };

  const onBack = () => setStep(step - 1);

  const onPreview = () => {
    if (!content || content.trim() === "") {
      message.error("Please input content");
      return;
    }
    setStep(3);
  };

  // Build full preview HTML combining template + editor content for the preview step
  const buildFullPreviewHtml = () => {
    const previewTemplate = buildPreviewHtml();
    const editorContent = content.replace(templateContent, '').trim();
    let html = previewTemplate;
    if (editorContent) {
      html += `<div>${editorContent}</div>`;
    }
    return html;
  };

  return (
    <Modal
      open={show}
      destroyOnClose={true}
      onCancel={() => {
        proceed(null);
      }}
      className="message-dlg"
      closable={true}
      maskClosable={false}
      title={step === 1 ? "Send Mail" : step === 2 ? "Email Content" : "Preview Email"}
      footer={null}
    >
      {step === 1 && (
        <>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label">From:</div>
            <Input
              className="custom-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="Sender name or email"
            />
          </Row>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label required">Subject</div>
            <Input 
              className="custom-input" 
              value={subject} 
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
            />
          </Row>
          <Row style={{ marginTop: 10 }}>
            <div className="field-label required">Audiences</div>
            <Select
              mode="multiple"
              placeholder="Select audiences"
              allowClear
              value={selectedToAudiences}
              onChange={(val) => {
                if (val && val.includes("__create_new__")) {
                  setShowCreateAudience(true);
                  // Remove the special value from selection
                  setSelectedToAudiences(val.filter((v) => v !== "__create_new__"));
                } else {
                  setSelectedToAudiences(val || []);
                }
              }}
              style={{ width: "100%" }}
            >
              <Select.Option key="__create_new__" value="__create_new__" style={{ borderBottom: "1px solid #f0f0f0", color: "#1890ff", fontWeight: 600 }}>
                + Create New Audience
              </Select.Option>
              {audienceList.map((a) => (
                <Select.Option key={a.id} value={a.id}>
                  {a.name} ({a.email_count || 0} recipients)
                </Select.Option>
              ))}
            </Select>
          </Row>
          <CreateAudienceModal
            visible={showCreateAudience}
            onCancel={() => setShowCreateAudience(false)}
            onSubmit={handleCreateAudienceSubmit}
            mode="create"
            initialData={null}
            token={props.token}
            clubId={props.club_id}
          />
          <Row style={{ marginTop: 10 }}>
            <div style={{ marginBottom: 8, fontWeight: 600, width: '100%' }}>Template</div>
            <div className="template-grid">
              {[
                { key: 'logo-with-message', label: 'Logo with Message', icon: <><PictureOutlined /> <FileTextOutlined /></> },
                { key: 'logo-with-image', label: 'Logo with Image', icon: <PictureOutlined /> },
                { key: 'logo-with-image-and-message', label: 'Logo with Image and Message', icon: <><PictureOutlined /> <FileTextOutlined /></> },
                { key: 'message-only', label: 'Message only', icon: <FileTextOutlined /> },
              ].map(t => (
                <div
                  key={t.key}
                  className={`template-card ${templateType === t.key ? 'selected' : ''}`}
                  onClick={() => setTemplateType(t.key)}
                >
                  <div className="template-thumb template-icons">{t.icon}</div>
                  <div className="template-title">{t.label}</div>
                </div>
              ))}
            </div>
          </Row>
          {/* Only show logo upload for templates that require logos */}
          {(templateType === "logo-with-message" || templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && (
            <div className="upload-section">
              <div className="message-upload-row">
                <div className="label-left">Upload Logo</div>
                <Checkbox checked={useDefaultLogo} onChange={(e) => { setUseDefaultLogo(e.target.checked); if (e.target.checked) { setLogoFile(null); } }}>
                  Use default
                </Checkbox>
                <label className={`custom-button common-button ${useDefaultLogo ? "disabled-button" : ""}`} style={useDefaultLogo ? { opacity: 0.5, pointerEvents: "none" } : {}}>
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={onUploadLogo} disabled={useDefaultLogo} />
                  Choose File
                </label>
                {useDefaultLogo && defaultLogoUrl && (
                  <img className="preview-thumb" src={defaultLogoUrl} alt="default logo" />
                )}
                {!useDefaultLogo && logoFile && <>
                  <img className="preview-thumb" src={logoBlobUrl || URL.createObjectURL(logoFile)} alt="logo" />
                  <span className="file-name">{logoFile.name}</span>
                </>}
              </div>
              {useDefaultLogo && !defaultLogoUrl && (
                <p style={{ color: "#f5222d", fontSize: 12, margin: "4px 0 0 0" }}>
                  No default logo set. Go to Custom Webpage to set a default email logo.
                </p>
              )}
            </div>
          )}
          {/* Only show image upload for templates that require images */}
          {(templateType === "logo-with-image" || templateType === "logo-with-image-and-message") && (
            <div className="upload-section">
              <div className="message-upload-row">
                <div className="label-left">Upload Image</div>
                <label className="custom-button common-button">
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={onUploadAsset} />
                  Choose File
                </label>
                {assetFile && <>
                  {assetFile.type.startsWith('image/') && (
                    <img className="preview-thumb" src={assetBlobUrl || URL.createObjectURL(assetFile)} alt="asset" />
                  )}
                  <span className="file-name">{assetFile.name}</span>
                </>}
              </div>
            </div>
          )}
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button onClick={() => proceed(null)} style={{ marginRight: 8 }}>Cancel</Button>
            <Button type="primary" onClick={onNext}>Next</Button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div style={{ marginTop: 10 }}>
            <Card size="small" style={{ border: '1px solid #e8e8e8' }}>
              <div dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }} />
            </Card>
          </div>
          {(templateType === 'logo-with-message' || templateType === 'logo-with-image-and-message' || templateType === 'message-only') && (
            <div className="editor-rect" style={{ marginTop: 10 }}>
              <Editor
                editorState={editorState}
                toolbarClassName="toolbarClassName"
                wrapperClassName="wrapperClassName"
                editorClassName="editorClassName"
                onEditorStateChange={onEditorStateChange}
              />
            </div>
          )}
          <div className="new_image_uploader">
            <label htmlFor="new_image_uploader">
              <input type="file" id="new_image_uploader" name="new_image_uploader" style={{ display: "none" }} accept="*" onChange={onDropImage} />
              <span className="attach-file-button">Attach File</span>
            </label>
            {selFile && (
              <div className="attached-file-info">
                <div className="attached-file-icon">
                  <FileTextOutlined style={{ fontSize: '16px', color: '#6c757d' }} />
                </div>
                <div className="attached-file-details">
                  <div className="attached-file-name">
                    {selFile.name}
                  </div>
                  <div className="attached-file-meta">
                    <span>{selFile.type || 'Unknown type'}</span>
                    <span>•</span>
                    <span>{selFile.size ? `${(selFile.size / 1024).toFixed(1)} KB` : 'Unknown size'}</span>
                  </div>
                </div>
                <CloseOutlined 
                  className="attached-file-remove"
                  onClick={() => setSelFile(null)}
                />
              </div>
            )}
            {/* debug log removed */}
          </div>
          <div className="message-modal-step2-buttons full-width">
            <Button onClick={onBack}>Back</Button>
            <Button type="primary" onClick={onPreview}>Preview</Button>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <div style={{ marginTop: 10, marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>Subject: <span style={{ fontWeight: 400 }}>{subject}</span></div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              To: <span style={{ fontWeight: 400 }}>
                {selectedToAudiences.map((id) => {
                  const aud = audienceList.find((a) => a.id === id);
                  return aud ? aud.name : id;
                }).join(", ")}
              </span>
            </div>
            {selFile && (
              <div style={{ marginBottom: 8, fontWeight: 600 }}>
                Attachment: <span style={{ fontWeight: 400 }}>{selFile.name}</span>
              </div>
            )}
          </div>
          <Card
            size="small"
            style={{
              border: "1px solid #d9d9d9",
              borderRadius: 8,
              maxHeight: 400,
              overflow: "auto",
              background: "#fff",
            }}
          >
            <div dangerouslySetInnerHTML={{ __html: buildFullPreviewHtml() }} />
          </Card>
          <div className="message-modal-step2-buttons full-width" style={{ marginTop: 16 }}>
            <Button onClick={onBack}>Back</Button>
            <Button
              type="primary"
              onClick={onCreate}
              loading={isSending}
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Send"}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default confirmable(MessageModal);
